import React, { useState, useEffect, useRef, useMemo } from 'react';

import 'font-awesome/css/font-awesome.min.css';
import '../../assets/stylesheet/layout.scss';
import '../../assets/stylesheet/textwordcloud.scss';
import Button from 'react-bootstrap/Button';
import { Nav, Tab, Row, Col } from 'react-bootstrap';
import DashedCircleProgressBar from '../../staticComps/dashedcircleprogressbar';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import * as d3 from 'd3';
import StackedSingleBarChart, { StackedSingleBarChart1 } from '../../staticComps/stackedSingleBarChart';
import ApiRequest from '../../services/apiService';

import baseUrl from '../../services/baseUrl';
import WordCloud from 'react-d3-cloud';
import ScreenLoader from '../../components/screenLoader';
import GetUserData from '../../utilities/getUserDetails';

function TextWordCloud() {
    const svgRef = useRef(null);
    const [key, setKey] = useState('key-words');
    const [isScrolled, setIsScrolled] = useState(false);
    //-----------------------------------



    const [searchTerm, setSearchTerm] = useState(localStorage.getItem('wordCloudSearchTerm') || '');
    const [selectedFilters, setSelectedFilters] = useState(localStorage.getItem('wordCloudSelectedFilters') || []);
    const [userData, setUserData] = useState(GetUserData() || {});
    const [quickFilter, setQuickFilter] = useState(localStorage.getItem('quickFilter') || '');
    const [projectFilter, setProjectFilter] = useState(localStorage.getItem('projectFilter') || '');
    const [touchpointFilter, setTouchpointFilter] = useState(localStorage.getItem('touchpointFilter') || '');
    const [sobFilter, setSobFilter] = useState(localStorage.getItem('sobFilter') || '');
    const [toDate, setToDate] = useState(localStorage.getItem('toDate') || '');
    const [fromDate, setFromDate] = useState(localStorage.getItem('fromDate') || '');
    const [wordCloudData, setWordCloudData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchWordCloudData = async (searchTerm, selectedFilters) => {
        selectedFilters = Array.isArray(selectedFilters) ? selectedFilters : [];
        localStorage.setItem('wordCloudSearchTerm', searchTerm);
        localStorage.setItem('wordCloudSelectedFilters', selectedFilters);
        setIsLoading(true);
        try {
            const filterValues = {
                Promoters: 10,
                Passives: 8,
                Detractors: 6,
            };

            const PpdFiltersQuery = selectedFilters
                .map((filter) => filterValues[filter])
                .filter((value) => value !== undefined)
                .join(",");

            const params = new URLSearchParams({
                searchTerm: searchTerm || "",
                filters: PpdFiltersQuery,
                quickFilterRange: quickFilter,
                queryMode: 7,
                fromDate: localStorage.getItem('fromDate') || fromDate,
                toDate: localStorage.getItem('toDate') || toDate,
                touchpointFilter: touchpointFilter,
                sobFilter: sobFilter,
                projectFilter: localStorage.getItem('projectFilter') || projectFilter,
                userRole: userData.UserRole,
                userProject: userData.ProjectIds,
                userCode: userData.UserCode,
            });

            const url = `${baseUrl()}/nps/GetNpsDashboardChartsData?${params.toString()}`;

            const responseData = await ApiRequest({
                apiLink: url,
                method: "GET",
            });
            if (responseData.IsSuccess && responseData.Data) {
                setWordCloudData(responseData.Data.WordCloudCharts || []);
                setIsLoading(false);
            } else {
                console.error("API Response Error:", responseData);
            }
        } catch (error) {
            console.error("Error fetching filtered word cloud data:", error);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchWordCloudData(searchTerm, selectedFilters);
        }, 1800);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, selectedFilters]);


    const handleFilterClick = (type) => {
        setSelectedFilters(prevFilters => {
            const newFilters = Array.isArray(prevFilters)
                ? (prevFilters.includes(type)
                    ? prevFilters.filter(filter => filter !== type)
                    : [...prevFilters, type])
                : [type];  
    
            fetchWordCloudData(searchTerm, newFilters);
            return newFilters;
        });
    };
    
    const handleClear = () => {
        setSearchTerm('');
        setSelectedFilters([]);
        fetchWordCloudData('', []);
        localStorage.removeItem('wordCloudSearchTerm');
        localStorage.removeItem('wordCloudSelectedFilters');
    };

    const categoryColors = {
        6: "#f10000",
        10: "#008000",
        8: "#ffb62f"
    };


    const categoryScore = {
        6: 60,
        10: 100,
        8: 80
    };

    const processedTags = wordCloudData.flatMap(entry => {
        const word = entry.Word.toLowerCase().replace(/[^a-z0-9]/gi, "");
        const color = categoryColors[entry.Rating] || "#ffb62f";
        const customerName = entry.CustomerName || "";
        const id = entry.id || "";
        return word ? { value: word, count: entry.Frequency, color, customerName, id } : null;
    }).filter(Boolean);

    const aggregatedTags = processedTags.reduce((acc, tag) => {
        if (acc[tag.value]) {
            acc[tag.value].count += tag.count;
        } else {
            acc[tag.value] = { ...tag };
        }
        return acc;
    }, {});

    const finalTags = Object.values(aggregatedTags);

    const handleTagClick = (word) => {
        setSearchTerm(word);
        fetchWordCloudData(word, selectedFilters);
    };

    //---------------------------------


    const handleScroll = () => {
        setIsScrolled(window.scrollY > 50);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);




    const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const highlightText = (text, highlight) => {
        if (!highlight) return text;

        const escapedHighlight = escapeRegExp(highlight);
        const regex = new RegExp(`(${escapedHighlight})`, 'gi');
        return text.replace(regex, (match) => `<span class="highlight">${match}</span>`);
    };
    const navigate = useNavigate();
    const CryptoJS = require('crypto-js');
    const secretKey = 'your-secret-key';
    const handleCustomerNameClick = async (userName, id) => {
        if (userName && id) {
            const encryptedUserId = CryptoJS.AES.encrypt(id.toString(), secretKey).toString();
            const encodedUserId = encodeURIComponent(encryptedUserId);
            const query = `tempid=${encodedUserId}`;
            navigate(`/ConversationPreview?${query}`);
        } else {
            console.log('User not available');
        }
    };

    return (
        <>
            {isLoading ? (
                <>
                    <div className="screenLoader">
                        <ScreenLoader />
                    </div>
                </>
            ) : (
                <>
                    <section className='container-fluid'>
    <div className='Page-title d-flex align-items-center mb-2'>
        <h3>Key-Words</h3>
        <div className='ms-auto'>
            <a href={'/home'} className="btn btn-primary">
                <i className="fa fa-chevron-left" aria-hidden="true"></i> Back
            </a>
        </div>
    </div>
    <div className='row g-1 justify-content-center'> {/* Add justify-content-center here to center the section */}
        {/* Filter Section */}
        <div className='col-12 col-sm-12 col-md-9 col-lg-8 col-xl-8'>
            <div className='card'>
                <div className='card-body'>
                    <div className='row g-2'>
                       
                        <div className="col-md-4">
                    <div className='row px-2'>
                        <div className='col-12 mb-2 text-center'>
                            <Button
                                variant={selectedFilters.includes('Promoters') ? "success ppdFiltersBtn" : "success"}
                                className={`btn-sm px-2 me-2 mb-2 ${selectedFilters.includes('Promoters') ? 'active' : 'inactive'}`}
                                onClick={() => handleFilterClick('Promoters')}
                            >
                                Promoters
                            </Button>
                            <Button
                                variant={selectedFilters.includes('Passives') ? "warning ppdFiltersBtn" : "warning"}
                                className={`btn-sm px-2 me-2 mb-2 ${selectedFilters.includes('Passives') ? 'active' : 'inactive'}`}
                                onClick={() => handleFilterClick('Passives')}
                            >
                                Passives
                            </Button>
                            <Button
                                variant={selectedFilters.includes('Detractors') ? "danger ppdFiltersBtn" : "danger"}
                                className={`btn-sm px-2 me-2 mb-2 ${selectedFilters.includes('Detractors') ? 'active' : 'inactive'}`}
                                onClick={() => handleFilterClick('Detractors')}
                            >
                                Detractors
                            </Button>
                        </div>
                        <div className='col-12'>
                            <div className="did-floating-label-content">
                                <input
                                    className="did-floating-input"
                                    type="search"
                                    placeholder=" "
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.currentTarget.value)}
                                />
                                <label className="did-floating-label">Keyword</label>
                            </div>
                        </div>
                        <div className='col-12 mb-2 text-center'>
                            <Button
                                variant="secondary"
                                className='btn-sm px-3 me-2 mb-2'
                                onClick={handleClear}
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>
                      
                        {/* Word Cloud Section */}
                        <div className="col-md-8">
                            <div className="row">
                                <div className="col-12 text-center">
                                    <WordCloud
                                        data={finalTags.map((tag) => ({
                                            text: tag.value,
                                            value: tag.count,
                                            color: tag.color
                                        }))}
                                        fontSize={(word) => Math.max(Math.log2(word.value) * 25, 30)}
                                        spiral="rectangular"
                                        rotate={()=>0}
                                        padding={10}
                                        fill={(d) => d.color}
                                        onWordClick={(event, d) => handleTagClick(d.text)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Customer Reviews Section - align to the right */}
        <div className='col-12 col-sm-12 col-md-3 col-lg-4 col-xl-4 ms-auto'> {/* Add ms-auto here */}
            <div className='card'>
                <div className='card-body p-1'>
                    <div className='card-content'>
                        <div className={isLoading ? 'd-none customer-reviews' : 'customer-reviews'}>
                            <div className='row'>
                                {Array.isArray(wordCloudData) && wordCloudData.length > 0 ? (
                                    wordCloudData.reduce((acc, review) => {
                                        if (!acc.find(item => item.CustomerName === review.CustomerName)) {
                                            acc.push(review);
                                        }
                                        return acc;
                                    }, []).map((review, index) => (
                                        <div key={index} className='customer-review'>
                                            <div className='progress-bar'>
                                                <DashedCircleProgressBar
                                                    value={categoryScore[review.Rating]}
                                                    text={categoryScore[review.Rating]}
                                                    color={categoryColors[review.Rating]}
                                                    textColor={categoryColors[review.Rating]}
                                                />
                                            </div>
                                            <div className='comment-wrapper'>
                                                <div className='customer-info'>
                                                    <a className='customer-name' onClick={() => handleCustomerNameClick(review.CustomerName, review.id)}>
                                                        {review.CustomerName}
                                                    </a>
                                                    <p className='lab-number'>{review.LabNumber}</p>
                                                </div>
                                                <div
                                                    className='customer-comment'
                                                    dangerouslySetInnerHTML={{ __html: highlightText(review.Suggestions, searchTerm) }}
                                                />
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p>No customer reviews available.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>

                </>
            )}

        </>
    )

}


export default TextWordCloud